import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DigestSettings } from "@/components/dashboard/DigestSettings";
import { Trash2, Trophy, UserPlus, SlidersHorizontal } from "lucide-react";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const inputCls = "bg-[#0B0F17] border-[#334155] text-slate-100 placeholder:text-slate-500";
const TRACK_LABEL = { auto_counter: "Auto · Counter Sales", auto_subdealer: "Auto · Subdealer Sales", manual: "Manual entry" };

export const TargetsPerformance = ({ isAdmin }) => {
  const [month, setMonth] = useState(thisMonth());
  const [targets, setTargets] = useState([]);
  const [params, setParams] = useState([]);
  const [wl, setWl] = useState([]);
  const [tForm, setTForm] = useState({ employee_name: "", param: "", target_value: "" });
  const [pForm, setPForm] = useState({ name: "", tracking: "manual" });
  const [wlForm, setWlForm] = useState({ email: "", name: "" });
  const [actuals, setActuals] = useState({});

  const load = useCallback(() => {
    api.get(`/targets2?month=${month}`).then((r) => setTargets(r.data)).catch(() => {});
  }, [month]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/target-params").then((r) => setParams(r.data)).catch(() => {});
    if (isAdmin) api.get("/auth/whitelist").then((r) => setWl(r.data)).catch(() => {});
  }, [isAdmin]);

  const saveTarget = async (e) => {
    e.preventDefault();
    try {
      await api.post("/targets2", { employee_name: tForm.employee_name, param: tForm.param, month, target_value: Number(tForm.target_value || 0) });
      toast.success("Target saved");
      setTForm({ employee_name: "", param: "", target_value: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const saveActual = async (t) => {
    const v = actuals[t.id];
    if (v === undefined) return;
    await api.post(`/targets2/${t.id}/actual`, { value: Number(v || 0) });
    toast.success("Actual updated");
    load();
  };

  const addParam = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/target-params", pForm);
      setParams([...params, data]);
      setPForm({ name: "", tracking: "manual" });
      toast.success("Parameter added");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const addWhitelist = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/whitelist", wlForm);
      setWl([...wl, data]);
      setWlForm({ email: "", name: "" });
      toast.success("Employee added (whitelisted)");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const byEmployee = targets.reduce((a, t) => { (a[t.employee_name] = a[t.employee_name] || []).push(t); return a; }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <label className="text-xs uppercase tracking-widest text-slate-400">Month</label>
        <Input data-testid="performance-month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={`${inputCls} w-44 h-9`} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-[#1E293B] border border-[#334155] rounded-lg p-5" data-testid="performance-board">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <Trophy className="h-5 w-5 text-amber-400" /> Target Achievement — {month}
          </h3>
          <div className="space-y-5">
            {Object.entries(byEmployee).map(([emp, items]) => (
              <div key={emp} data-testid={`employee-targets-${emp.replace(/\s+/g, "-").toLowerCase()}`} className="rounded-lg bg-[#111827] border border-[#334155] p-4">
                <p className="font-bold text-slate-100 mb-3">{emp}</p>
                <div className="space-y-3">
                  {items.map((t) => (
                    <div key={t.id}>
                      <div className="flex flex-wrap items-center justify-between gap-2 text-xs mb-1">
                        <span className="text-slate-300">{t.param}
                          <Badge variant="outline" className={`ml-2 text-[9px] ${t.tracking === "manual" ? "border-amber-500/50 text-amber-400" : "border-emerald-500/50 text-emerald-400"}`}>
                            {TRACK_LABEL[t.tracking]}
                          </Badge>
                        </span>
                        <span className="flex items-center gap-2 font-mono">
                          <span className="text-slate-200">{t.actual}</span><span className="text-slate-500">/ {t.target_value}</span>
                          <span className={t.pct >= 100 ? "text-emerald-400" : "text-amber-400"}>{t.pct}%</span>
                          {isAdmin && t.tracking === "manual" && (
                            <span className="flex items-center gap-1">
                              <Input data-testid={`actual-input-${t.id}`} type="number" placeholder="actual" defaultValue={t.actual || ""}
                                onChange={(e) => setActuals({ ...actuals, [t.id]: e.target.value })}
                                className={`${inputCls} h-6 w-20 text-xs`} />
                              <Button size="sm" data-testid={`actual-save-${t.id}`} onClick={() => saveActual(t)} className="h-6 px-2 text-[10px] bg-amber-600 hover:bg-amber-500 text-white">Set</Button>
                            </span>
                          )}
                          {isAdmin && (
                            <button data-testid={`delete-target2-${t.id}`} onClick={async () => { await api.delete(`/targets2/${t.id}`); load(); }}
                              className="text-slate-600 hover:text-rose-400"><Trash2 className="h-3 w-3" /></button>
                          )}
                        </span>
                      </div>
                      <Progress value={Math.min(t.pct, 100)} className="h-2 bg-slate-800" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {targets.length === 0 && <p className="text-sm text-slate-500 py-6 text-center">{isAdmin ? "No targets set for this month yet — use the form on the right." : "Your admin hasn't set targets for you this month."}</p>}
          </div>
          {targets.some((t) => t.tracking !== "manual") && (
            <p className="text-[10px] text-slate-500 mt-4">Auto-tracked params (Counter/Subdealer Sale) use dealership-wide delivery counts from the sheet for the month.</p>
          )}
        </div>
        <div className="space-y-6">
          {isAdmin && (
            <div data-testid="set-target-form" className="bg-[#1E293B] border border-[#334155] rounded-lg p-5">
              <h3 className="text-base font-semibold mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Set Target · {month}</h3>
              <form onSubmit={saveTarget} className="space-y-3">
                <Select value={tForm.employee_name} onValueChange={(v) => setTForm({ ...tForm, employee_name: v })}>
                  <SelectTrigger data-testid="target-employee-select" className={inputCls}><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#334155] text-slate-100">
                    {wl.filter((w) => w.name).map((w) => <SelectItem key={w.id} value={w.name}>{w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={tForm.param} onValueChange={(v) => setTForm({ ...tForm, param: v })}>
                  <SelectTrigger data-testid="target-param-select" className={inputCls}><SelectValue placeholder="Select parameter" /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#334155] text-slate-100">
                    {params.map((p) => <SelectItem key={p.id} value={p.name}>{p.name} · {TRACK_LABEL[p.tracking]}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input data-testid="target-value-input" type="number" min="0" placeholder="Target value" value={tForm.target_value}
                  onChange={(e) => setTForm({ ...tForm, target_value: e.target.value })} className={inputCls} />
                <Button data-testid="save-target-btn" type="submit" disabled={!tForm.employee_name || !tForm.param}
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white active:scale-95 transition-colors">Save Target</Button>
              </form>
            </div>
          )}
          {isAdmin && (
            <div data-testid="params-panel" className="bg-[#1E293B] border border-[#334155] rounded-lg p-5">
              <h3 className="text-base font-semibold mb-1 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                <SlidersHorizontal className="h-4 w-4 text-purple-400" /> Target Parameters
              </h3>
              <p className="text-xs text-slate-400 mb-3">Define what targets are measured on & how they're tracked.</p>
              <div className="space-y-1.5 mb-3 max-h-44 overflow-auto">
                {params.map((p, i) => (
                  <div key={p.id} data-testid={`param-row-${i}`} className="flex items-center justify-between text-xs py-1 border-b border-slate-800/60">
                    <span className="text-slate-200">{p.name}</span>
                    <span className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-[9px] ${p.tracking === "manual" ? "border-amber-500/50 text-amber-400" : "border-emerald-500/50 text-emerald-400"}`}>{TRACK_LABEL[p.tracking]}</Badge>
                      <button data-testid={`delete-param-${i}`} onClick={async () => { await api.delete(`/target-params/${p.id}`); setParams(params.filter((x) => x.id !== p.id)); }}
                        className="text-slate-600 hover:text-rose-400"><Trash2 className="h-3 w-3" /></button>
                    </span>
                  </div>
                ))}
              </div>
              <form onSubmit={addParam} className="space-y-2">
                <Input data-testid="param-name-input" required placeholder="New parameter name" value={pForm.name}
                  onChange={(e) => setPForm({ ...pForm, name: e.target.value })} className={`${inputCls} h-9`} />
                <Select value={pForm.tracking} onValueChange={(v) => setPForm({ ...pForm, tracking: v })}>
                  <SelectTrigger data-testid="param-tracking-select" className={`${inputCls} h-9`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#334155] text-slate-100">
                    <SelectItem value="manual">Manual entry (admin)</SelectItem>
                    <SelectItem value="auto_counter">Auto · Counter Sales</SelectItem>
                    <SelectItem value="auto_subdealer">Auto · Subdealer Sales</SelectItem>
                  </SelectContent>
                </Select>
                <Button data-testid="add-param-btn" type="submit" className="w-full h-9 bg-purple-600 hover:bg-purple-500 text-white text-xs active:scale-95 transition-colors">Add Parameter</Button>
              </form>
            </div>
          )}
          {isAdmin && (
            <div data-testid="whitelist-panel" className="bg-[#1E293B] border border-[#334155] rounded-lg p-5">
              <h3 className="text-base font-semibold mb-1 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                <UserPlus className="h-4 w-4 text-blue-400" /> Add Employees
              </h3>
              <p className="text-xs text-slate-400 mb-4">Whitelisted emails can register & log in.</p>
              <form onSubmit={addWhitelist} className="space-y-3 mb-4">
                <Input data-testid="whitelist-email-input" required type="email" placeholder="employee@email.com" value={wlForm.email}
                  onChange={(e) => setWlForm({ ...wlForm, email: e.target.value })} className={inputCls} />
                <Input data-testid="whitelist-name-input" required placeholder="Employee name" value={wlForm.name}
                  onChange={(e) => setWlForm({ ...wlForm, name: e.target.value })} className={inputCls} />
                <Button data-testid="add-whitelist-btn" type="submit" className="w-full bg-slate-700 hover:bg-slate-600 text-white active:scale-95 transition-colors">Add Employee</Button>
              </form>
              <div className="space-y-2 max-h-40 overflow-auto">
                {wl.map((w, i) => (
                  <div key={w.id} data-testid={`whitelist-row-${i}`} className="flex items-center justify-between text-xs py-1.5 border-b border-slate-800/60">
                    <div>
                      <p className="text-slate-200">{w.name || w.email}</p>
                      <p className="text-slate-500">{w.email} {w.registered && <span className="text-emerald-400">· registered</span>}</p>
                    </div>
                    <button data-testid={`remove-whitelist-btn-${i}`} onClick={async () => { await api.delete(`/auth/whitelist/${w.id}`); setWl(wl.filter((x) => x.id !== w.id)); }}
                      className="text-slate-500 hover:text-rose-400 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {isAdmin && <DigestSettings />}
          {!isAdmin && (
            <div className="bg-[#1E293B] border border-[#334155] rounded-lg p-5 text-sm text-slate-400">
              Targets are managed by your admin (in Settings). You see only your own targets here.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
